import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";

import { API_BASE_URL } from "../config/apiBase";
import { getVerificationOrigin } from "../config/verificationOrigin";
import { studentLessonPathFor } from "../utils/lessonRoute";
import { addDownloadCacheBuster } from "../utils/certificateDownloadUrl";

const API = API_BASE_URL;

function formatTime(value) {
  if (!value) return "";
  const raw = String(value);
  const normalized = raw.includes(" ") && !raw.includes("T") ? `${raw.replace(" ", "T")}Z` : raw;
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? raw : d.toLocaleString();
}

function lessonMeta(note) {
  const parts = [];
  if (note?.week !== null && note?.week !== undefined && note?.week !== "") parts.push(`Week ${note.week}`);
  if (note?.lesson !== null && note?.lesson !== undefined && note?.lesson !== "") parts.push(`Lesson ${note.lesson}`);
  return parts.join(" · ");
}

export default function StudentPortalHome() {
  const navigate = useNavigate();
  const {
    studentId,
    studentName,
    authRole,
    isAdmin,
    todayLabel,
    goToDashboardV2,
    goToLessons,
    goToCurrentLesson,
    goToGuidedDemo,
    goToTutorNotes,
    currentWeek,
    currentLesson,
    currentLessonId,
    currentLessonPath,
  } =
    useOutletContext();
  const sid = useMemo(() => String(studentId || "").toUpperCase(), [studentId]);
  const [latestNote, setLatestNote] = useState(null);
  const [loadingLatest, setLoadingLatest] = useState(false);
  const [certificateState, setCertificateState] = useState(null);
  const [certificateLoading, setCertificateLoading] = useState(false);
  const [certificateError, setCertificateError] = useState("");
  const resolvedWeek = Number(currentWeek) || 1;
  const resolvedLesson = Number(currentLesson) || 1;
  const continueUrl = currentLessonPath || studentLessonPathFor(sid, 1, 1);
  const showCertificateGenerator = Boolean(isAdmin || String(authRole || "").toLowerCase() === "admin");
  const showAdminPanel = showCertificateGenerator;

  useEffect(() => {
    if (process.env.NODE_ENV === "production") return;
    console.log("STUDENT_DASHBOARD_CONTINUE_DEBUG", {
      studentId: sid,
      currentLessonId: currentLessonId || "W1L1",
      resolvedWeek,
      resolvedLesson,
      continueUrl,
    });
  }, [sid, currentLessonId, resolvedWeek, resolvedLesson, continueUrl]);

  useEffect(() => {
    if (!sid) return;
    let cancelled = false;

    async function loadLatestNote() {
      setLoadingLatest(true);
      try {
        const res = await fetch(`${API}/api/student/${sid}/tutor-notes/latest`, {
          cache: "no-store",
        });
        const json = await res.json();
        if (!cancelled) setLatestNote(json?.latest || null);
      } catch (e) {
        if (!cancelled && e?.name !== "AbortError") setLatestNote(null);
      } finally {
        if (!cancelled) setLoadingLatest(false);
      }
    }

    loadLatestNote();
    return () => {
      cancelled = true;
    };
  }, [sid]);

  const generateTestCertificate = async () => {
    if (!showCertificateGenerator || certificateLoading) return;
    setCertificateLoading(true);
    setCertificateError("");
    const downloadToken = `${Date.now()}`;
    try {
      const res = await fetch(`${API}/api/admin/students/${encodeURIComponent(sid)}/certificate/generate-test`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-User": sid,
        },
        body: JSON.stringify({
          verification_origin: getVerificationOrigin(),
        }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || json?.ok === false) {
        throw new Error((json && (json.detail || json.error)) || "Failed to generate test certificate.");
      }
      setCertificateState({
        ...json,
        download_url: addDownloadCacheBuster(json.download_url, downloadToken),
      });
    } catch (error) {
      setCertificateError(error instanceof Error ? error.message : "Failed to generate test certificate.");
    } finally {
      setCertificateLoading(false);
    }
  };

  return (
    <section className="sp-content">
      <div className="sp-hero sp-card reveal" style={{ "--delay": ".05s" }}>
        <div className="sp-hero-date">{todayLabel}</div>
        <div className="sp-hero-title">Welcome back, {studentName}!</div>
        <div className="sp-hero-sub">Pick up where you left off in your writing journey.</div>
      </div>

      <div className="sp-columns">
        <section className="sp-cards" aria-label="Quick actions">
          <div className="sp-card sp-action reveal" style={{ "--delay": ".1s" }}>
            <div>
              <h2 className="sp-h2">Continue</h2>
              <p className="sp-text">Jump back into your next writing session.</p>
            </div>
            <button onClick={goToCurrentLesson} className="sp-cta">
              Go to Week {resolvedWeek} · Lesson {resolvedLesson} →
            </button>
          </div>

          <div className="sp-card sp-action reveal" style={{ "--delay": ".18s" }}>
            <div>
              <h2 className="sp-h2">Progress</h2>
              <p className="sp-text">See your weeks, submissions, and AI feedback in one place.</p>
            </div>
            <button onClick={goToDashboardV2} className="sp-cta ghost">
              Open progress →
            </button>
          </div>

          <div className="sp-card sp-action reveal" style={{ "--delay": ".26s" }}>
            <div>
              <h2 className="sp-h2">Lessons overview</h2>
              <p className="sp-text">Browse lessons and track your progress.</p>
            </div>
            <button onClick={goToLessons} className="sp-cta ghost">
              Go to lessons →
            </button>
          </div>

          <div className="sp-card sp-action reveal" style={{ "--delay": ".34s" }}>
            <div>
              <h2 className="sp-h2">Writeflow Guided Demo</h2>
              <p className="sp-text">Learn how Writeflow works before you write.</p>
            </div>
            <button onClick={goToGuidedDemo} className="sp-cta ghost">
              Run demo →
            </button>
          </div>

          {showAdminPanel ? (
            <div className="sp-card sp-action reveal" style={{ "--delay": ".38s" }}>
              <div>
                <h2 className="sp-h2">User Admin</h2>
                <p className="sp-text">Manage enrollment requests, users, cohorts, and audit records.</p>
              </div>
              <button onClick={() => navigate("/admin")} className="sp-cta ghost">
                Open Admin Panel →
              </button>
            </div>
          ) : null}

          {showAdminPanel ? (
            <div className="sp-card sp-action reveal" style={{ "--delay": ".42s" }}>
              <div>
                <h2 className="sp-h2">Student Progress</h2>
                <p className="sp-text">View enrolled students and open each student’s full progress.</p>
              </div>
              <button type="button" onClick={() => navigate("/tutor/student-progress")} className="sp-cta ghost">
                Open Student Progress →
              </button>
            </div>
          ) : null}

          {showCertificateGenerator ? (
            <div className="sp-card sp-action reveal" style={{ "--delay": ".46s" }}>
              <div>
                <h2 className="sp-h2">Certificate Generator</h2>
                <p className="sp-text">Generate and preview HIRI certificates using the current template and student data.</p>
              </div>
            <button type="button" onClick={generateTestCertificate} className="sp-cta ghost" disabled={certificateLoading}>
                {certificateLoading ? "Generating..." : "Generate Test Certificate"}
              </button>
              {certificateError ? <p className="sp-text" style={{ color: "#fca5a5" }}>{certificateError}</p> : null}
              {certificateState ? (
                <div className="sp-text" style={{ display: "grid", gap: 8 }}>
                  <div><b>Certificate ID:</b> {certificateState.certificate_id}</div>
                  <a href={`${API}/api/certificates/verify/${encodeURIComponent(certificateState.certificate_id)}`} target="_blank" rel="noreferrer">
                    View JSON
                  </a>
                  <a href={`${API}${certificateState.download_url}`} target="_blank" rel="noreferrer">
                    Download Certificate
                  </a>
                  <a href={certificateState.verification_url} target="_blank" rel="noreferrer">
                    Open Verification Page
                  </a>
                </div>
              ) : null}
            </div>
          ) : null}
        </section>

        <aside className="sp-right" aria-label="Notes">
          <div className="sp-card sp-note reveal" style={{ "--delay": ".16s" }}>
            <h2 className="sp-h2">Shaanzae’s Latest Note</h2>
            {loadingLatest ? (
              <p className="sp-text">Loading…</p>
            ) : latestNote ? (
              <>
                <p className="sp-text" style={{ whiteSpace: "pre-wrap" }}>
                  {latestNote.note}
                </p>
                <p className="sp-text" style={{ fontSize: 12, opacity: 0.75 }}>
                  {formatTime(latestNote.created_at)}
                  {lessonMeta(latestNote) ? ` · ${lessonMeta(latestNote)}` : ""}
                </p>
              </>
            ) : (
              <p className="sp-text">No tutor guidance yet.</p>
            )}
            <button className="sp-cta ghost" type="button" onClick={goToTutorNotes}>
              View all notes →
            </button>
          </div>
        </aside>
      </div>
    </section>
  );
}
